
import styles from './Skeleton.module.scss';

export interface SkeletonProps {
  variant?: 'text' | 'circle' | 'rectangle' | 'rounded';
  width?: string | number;
  height?: string | number;
  className?: string;
}

export function Skeleton({
  variant = 'text',
  width,
  height,
  className,
}: SkeletonProps) {
  const style: React.CSSProperties = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
  };

  return (
    <div
      className={`${styles.skeleton} ${styles[`skeleton--${variant}`]} ${className || ''}`}
      style={style}
    />
  );
}

export interface WidgetSkeletonProps {
  showHeader?: boolean;
  linesCount?: number;
}

export function WidgetSkeleton({
  showHeader = true,
  linesCount = 3,
}: WidgetSkeletonProps) {
  return (
    <div className={styles.widgetSkeleton}>
      {showHeader && (
        <div className={styles.widgetSkeleton__header}>
          <Skeleton variant="text" width="40%" height={24} />
          <Skeleton variant="text" width="60%" height={16} />
        </div>
      )}
      <div className={styles.widgetSkeleton__content}>
        {Array.from({ length: linesCount }).map((_, index) => (
          <Skeleton
            key={index}
            variant="rounded"
            width="100%"
            height={60}
          />
        ))}
      </div>
    </div>
  );
}

export interface ChartSkeletonProps {
  type?: 'bar' | 'line' | 'pie' | 'heatmap' | 'radar';
  height?: number;
}

export function ChartSkeleton({ type = 'bar', height = 300 }: ChartSkeletonProps) {
  return (
    <div className={styles.chartSkeleton} style={{ height: `${height}px` }}>
      {type === 'bar' && (
        <div className={styles.chartSkeleton__bars}>
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton
              key={index}
              variant="rounded"
              width="100%"
              height={`${Math.random() * 60 + 40}%`}
            />
          ))}
        </div>
      )}
      {type === 'line' && (
        <div className={styles.chartSkeleton__line}>
          <Skeleton variant="rounded" width="100%" height="100%" />
        </div>
      )}
      {type === 'pie' && (
        <div className={styles.chartSkeleton__pie}>
          <Skeleton variant="circle" width={200} height={200} />
        </div>
      )}
      {type === 'heatmap' && (
        <div className={styles.chartSkeleton__heatmap}>
          {Array.from({ length: 7 }).map((_, rowIndex) => (
            <div key={rowIndex} className={styles.chartSkeleton__heatmapRow}>
              {Array.from({ length: 12 }).map((_, colIndex) => (
                <Skeleton
                  key={colIndex}
                  variant="rounded"
                  width={24}
                  height={24}
                />
              ))}
            </div>
          ))}
        </div>
      )}
      {type === 'radar' && (
        <div className={styles.chartSkeleton__radar}>
          <Skeleton variant="circle" width={250} height={250} />
        </div>
      )}
    </div>
  );
}
